import { Flex, Heading, Stack, Text, HStack } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { StatusBadge } from '@chakra-composites/status-badge';
import { Button } from '@chakra-primitives/button';
import { Switch } from '@chakra-primitives/switch';
import { toaster } from '@chakra-primitives/toaster';

export type ToasterType = 'success' | 'error' | 'warning' | 'info';

// Default toaster duration (5 seconds)
const DEFAULT_TOASTER_DURATION = 5000;

export const createToaster = (
  type: ToasterType,
  title: string,
  description?: string,
  duration?: number,
) => {
  toaster.create({
    title,
    description,
    type,
    duration: duration ?? DEFAULT_TOASTER_DURATION,
  });
};

type LayoutAdminHeaderProps = {
  title: string;
  description?: string | React.ReactNode;
  isEditing?: boolean;
  isSaving?: boolean;
  /**
   * Save button handler. Renders a "Save" button that is disabled when isEditing=false.
   * Mutually exclusive with `actions` prop - use one or the other, not both.
   */
  onSave?: (toaster: typeof createToaster) => void;
  /**
   * Disables the Save button even while editing, e.g. when the form is invalid.
   */
  saveDisabled?: boolean;
  /**
   * Cancel button handler. Renders a "Cancel" button with outline variant.
   * Mutually exclusive with `actions` prop - use one or the other, not both.
   */
  onCancel?: () => void;
  /**
   * Add button handler. Renders an "Add" button with solid variant.
   * Mutually exclusive with `actions` prop - use one or the other, not both.
   */
  onAdd?: (toaster: typeof createToaster) => void;
  /**
   * Back button handler. Navigates the user to the previous page.
   */
  onBack?: () => void;
  /**
   * Reset button handler. Renders a "Reset to default" button with ghost variant.
   * Mutually exclusive with `actions` prop - use one or the other, not both.
   */
  onReset?: () => void;
  saveButtonText?: string;
  cancelButtonText?: string;
  addButtonText?: string;
  backButtonText?: string;
  resetButtonText?: string;
  /**
   * Custom actions element to render instead of default buttons.
   * When provided, onSave/onCancel/onAdd are ignored.
   * Use this when you need full control over the actions section.
   */
  actions?: React.ReactNode;
  dataTestId?: string;
  /**
   * Current enabled/disabled state of the entire feature toggle.
   * When provided along with onFeatureStatusChange, renders a status badge and switch.
   */
  featureStatus?: boolean;
  /**
   * Handler for feature toggle changes. Receives { checked: boolean } details.
   * When provided along with featureStatus, renders a status badge and switch.
   */
  onFeatureStatusChange?: (details: { checked: boolean }) => void;
  /**
   * Display name for the feature (e.g., "SMS", "Email Notifications").
   * Must already be translated — a raw English literal ships mixed language.
   */
  featureName?: string;
  toaster?: typeof createToaster;
  /**
   * User who last edited. Displays "Last edited by {lastEditedBy}" above timestamp.
   */
  lastEditedBy?: string;
  /**
   * Timestamp of last edit. Displays below "Last edited by" text.
   */
  lastEditedAt?: string;
};

export const LayoutAdminPageHeader: React.FC<LayoutAdminHeaderProps> = ({
  title,
  description,
  isEditing = false,
  isSaving = false,
  onSave,
  saveDisabled = false,
  onCancel,
  onAdd,
  onBack,
  onReset,
  saveButtonText: saveButtonTextProp,
  cancelButtonText: cancelButtonTextProp,
  addButtonText: addButtonTextProp,
  backButtonText: backButtonTextProp,
  resetButtonText: resetButtonTextProp,
  actions,
  dataTestId,
  featureStatus,
  onFeatureStatusChange,
  featureName,
  toaster: customToaster,
  lastEditedBy,
  lastEditedAt,
}) => {
  const { t } = useTranslation();
  const toasterFunction = customToaster || createToaster;
  const saveButtonText = saveButtonTextProp ?? t('save');
  const cancelButtonText = cancelButtonTextProp ?? t('cancel');
  const addButtonText = addButtonTextProp ?? t('add');
  const backButtonText = backButtonTextProp ?? t('back');
  const resetButtonText = resetButtonTextProp ?? t('resetToDefault');
  const hasFeatureToggle = featureStatus !== undefined && onFeatureStatusChange;

  return (
    <Flex
      direction={{
        base: 'column',
        md: 'row',
      }}
      justify={{
        base: 'flex-start',
        md: 'space-between',
      }}
      align={{
        base: 'flex-start',
        md: 'flex-end',
      }}
      gap={{
        base: 3,
        md: 0,
      }}
      minH={description ? '64px' : undefined}
      pb={2}
      w="full"
      data-testid={dataTestId}
    >
      <Stack gap={0} pt={2} data-testid={`${dataTestId}-content`}>
        <Heading
          as="h1"
          size="xl"
          data-testid={`${dataTestId}-title`}
          title={title}
          maxW="70vw"
        >
          {title}
        </Heading>
        {typeof description === 'string' ? (
          <Text
            fontSize="sm"
            color="fg.muted"
            data-testid={`${dataTestId}-description`}
            title={description as string}
            maxW="70vw"
          >
            {description}
          </Text>
        ) : (
          description
        )}
        {lastEditedBy && lastEditedAt && (
          <Text
            fontSize="sm"
            color="fg.muted"
            data-testid={`${dataTestId}-last-edited`}
          >
            {t('lastEditedByOn', { editor: lastEditedBy, date: lastEditedAt })}
          </Text>
        )}
      </Stack>
      {(actions ||
        onSave ||
        onCancel ||
        onAdd ||
        onBack ||
        onReset ||
        hasFeatureToggle ||
        lastEditedBy) && (
        <HStack
          gap={4}
          mt={{
            base: 2,
            md: 0,
          }}
          justify="flex-end"
          w={{
            base: 'full',
            md: 'auto',
          }}
          data-testid={`${dataTestId}-actions`}
        >
          {/* If custom actions provided, use those; otherwise render default buttons */}
          {actions || (
            <>
              {hasFeatureToggle && (
                <Flex alignItems="center" gap={1}>
                  <StatusBadge
                    status={featureStatus ? 'success' : 'danger'}
                    data-testid={`${dataTestId}-feature-badge`}
                  >
                    {featureStatus
                      ? t('featureEnabled', { featureName })
                      : t('featureDisabled', { featureName })}
                  </StatusBadge>
                  <Switch
                    checked={featureStatus}
                    onCheckedChange={onFeatureStatusChange}
                    size="lg"
                    data-testid={`${dataTestId}-feature-switch`}
                    mr={2}
                  />
                </Flex>
              )}
              {onReset && (
                <Button
                  variant="ghost"
                  onClick={onReset}
                  data-testid={`${dataTestId}-reset-button`}
                >
                  {resetButtonText}
                </Button>
              )}
              {onCancel && (
                <Button
                  variant="outline"
                  onClick={onCancel}
                  data-testid={`${dataTestId}-cancel-button`}
                >
                  {cancelButtonText}
                </Button>
              )}
              {onSave && (
                <Button
                  variant="solid"
                  onClick={() => onSave(toasterFunction)}
                  disabled={!isEditing || saveDisabled}
                  loading={isSaving}
                  data-testid={`${dataTestId}-save-button`}
                >
                  {saveButtonText}
                </Button>
              )}
              {onAdd && (
                <Button
                  variant="solid"
                  onClick={() => onAdd(toasterFunction)}
                  data-testid={`${dataTestId}-add-button`}
                >
                  {addButtonText}
                </Button>
              )}
              {onBack && (
                <Button
                  variant="solid"
                  onClick={onBack}
                  data-testid={`${dataTestId}-back-button`}
                >
                  {backButtonText}
                </Button>
              )}
            </>
          )}
        </HStack>
      )}
    </Flex>
  );
};

export default LayoutAdminPageHeader;
